// DataTable.jsx
import { Table } from "@/design-system/native";
import { ChevronUp, ChevronDown } from "lucide-react";
import styles from "./DataTable.module.scss";

export default function DataTable({
  rows = [],
  columns = [],
  hiddenColumnIds = [],
  sortBy,
  sortDir,
  onSort,
  variant, // 👈 eklendi: tablo varyant adı (örn: "employee")
  className, // 👈 opsiyonel: ekstra sınıf geçmek istersen
  onRowClick,
  activeRowId = null,
  rowClassName,
  getRowProps,
}) {
  const hidden = new Set(hiddenColumnIds);
  const visibleCols = columns.filter((c) => !hidden.has(c.id));

  const handleSort = (col) => {
    if (!onSort || !col.accessor) return;
    if (sortBy === col.id) {
      onSort(col.id, sortDir === "asc" ? "desc" : "asc");
    } else {
      onSort(col.id, "asc");
    }
  };

  return (
    <div
      className={`de-table ${variant ? `de-table--${variant}` : ""} ${
        className || ""
      }`}
    >
      <Table
        responsive
        hover
        className={`${styles.table} ${
          variant ? `de-table__tbl--${variant}` : ""
        }`}
      >
        <thead>
          <tr>
            {visibleCols.map((col, ci) => {
              const isSorted = sortBy === col.id;
              return (
                <th
                  key={col.id ?? ci}
                  style={
                    col.width != null
                      ? {
                          "--col-width":
                            typeof col.width === "number"
                              ? `${col.width}px`
                              : col.width,
                        }
                      : undefined
                  }
                  onClick={() => handleSort(col)}
                  className={`f135 ${styles.th} ${
                    col.accessor ? styles.thSortable : ""
                  }`}
                >
                  <div className={styles.thInner}>
                    {col.header}
                    {isSorted &&
                      (sortDir === "asc" ? (
                        <ChevronUp size={14} className={styles.sortIcon} />
                      ) : (
                        <ChevronDown size={14} className={styles.sortIcon} />
                      ))}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const rowVariantClass =
              variant === "employee"
                ? ri % 2 === 1
                  ? "emp-row-even"
                  : "emp-row-odd"
                : "";
            const derivedRowClassName =
              typeof rowClassName === "function"
                ? rowClassName(row, ri)
                : rowClassName;
            const derivedRowProps =
              typeof getRowProps === "function" ? getRowProps(row, ri) || {} : {};
            const isRowClickable = Boolean(onRowClick || derivedRowProps.onClick);
            const composedClassName = [
              rowVariantClass,
              String(row?.id) === String(activeRowId)
                ? "de-table__row--active"
                : "",
              isRowClickable ? styles.rowClickable : "",
              derivedRowClassName,
              derivedRowProps.className,
            ]
              .filter(Boolean)
              .join(" ");
            const handleRowClick = () => {
              derivedRowProps.onClick?.(row, ri);
              onRowClick?.(row, ri);
            };
            return (
              <tr
                key={row?.id ?? ri}
                className={composedClassName}
                onClick={isRowClickable ? handleRowClick : undefined}
                onKeyDown={
                  isRowClickable
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleRowClick();
                        }
                      }
                    : undefined
                }
                tabIndex={isRowClickable ? 0 : undefined}
                role={isRowClickable ? "button" : undefined}
                style={derivedRowProps.style}
              >
                {visibleCols.map((col, ci) => {
                  const value =
                    typeof col.accessor === "function"
                      ? col.accessor(row)
                      : col.accessor
                      ? row?.[col.accessor]
                      : undefined;
                  return (
                    <td key={`${col.id ?? ci}-${ri}`} className="f135">
                      {typeof col.cell === "function"
                        ? col.cell(row, value, { rowIndex: ri, colIndex: ci })
                        : value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
